import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiCheck,
  FiClock,
  FiSave,
  FiSearch,
  FiUsers,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import {
  ATTENDANCE_STATUSES,
} from "../utils/employee.constants";

import {
  formatEmployeeLabel,
  getEmployeeApiError,
} from "../utils/employee.utils";

import "./EmployeeAttendance.css";


const getToday =
  () =>
    new Date()
      .toISOString()
      .slice(
        0,
        10
      );


const createAttendanceRows = (
  employees = []
) => {

  return employees.map(
    (
      employee
    ) => ({
      employeeId:
        employee._id,

      employeeCode:
        employee.employeeCode,

      name:
        employee.name,

      department:
        employee.department,

      status:
        "PRESENT",

      checkIn:
        "",

      checkOut:
        "",

      remark:
        "",
    })
  );

};


const BulkAttendanceModal = ({
  open,

  employees = [],

  onClose,

  onSuccess,
}) => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    attendanceDate,
    setAttendanceDate,
  ] =
    useState(
      getToday()
    );


  const [
    rows,
    setRows,
  ] =
    useState([]);


  const [
    search,
    setSearch,
  ] =
    useState("");


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  useEffect(
    () => {

      if (
        !open
      ) {

        return;

      }


      setAttendanceDate(
        getToday()
      );


      setRows(
        createAttendanceRows(
          employees
        )
      );


      setSearch(
        ""
      );


      setError(
        ""
      );

    },
    [
      open,
      employees,
    ]
  );


  if (
    !open
  ) {

    return null;

  }


  const filteredRows =
    rows.filter(
      (
        row
      ) => {

        const query =
          String(
            search ||
            ""
          )
            .trim()
            .toLowerCase();


        if (
          !query
        ) {

          return true;

        }


        return (
          String(
            row.name ||
            ""
          )
            .toLowerCase()
            .includes(
              query
            ) ||

          String(
            row.employeeCode ||
            ""
          )
            .toLowerCase()
            .includes(
              query
            ) ||

          String(
            row.department ||
            ""
          )
            .toLowerCase()
            .includes(
              query
            )
        );

      }
    );


  /*
  |--------------------------------------------------------------------------
  | ROW CHANGE
  |--------------------------------------------------------------------------
  */

  const updateRow = (
    employeeId,
    field,
    value
  ) => {

    setRows(
      (
        previous
      ) =>
        previous.map(
          (
            row
          ) => {

            if (
              String(
                row.employeeId
              ) !==
              String(
                employeeId
              )
            ) {

              return row;

            }


            const nextRow = {
              ...row,

              [field]:
                value,
            };


            /*
            |--------------------------------------------------------------------------
            | CLEAR TIME FOR NON WORKING STATUS
            |--------------------------------------------------------------------------
            */

            if (
              field ===
              "status" &&
              [
                "ABSENT",
                "PAID_LEAVE",
                "UNPAID_LEAVE",
                "HOLIDAY",
                "WEEK_OFF",
              ].includes(
                value
              )
            ) {

              nextRow.checkIn =
                "";

              nextRow.checkOut =
                "";

            }


            return nextRow;

          }
        )
    );

  };


  /*
  |--------------------------------------------------------------------------
  | MARK ALL
  |--------------------------------------------------------------------------
  */

  const markAll = (
    status
  ) => {

    setRows(
      (
        previous
      ) =>
        previous.map(
          (
            row
          ) => ({
            ...row,

            status,

            checkIn:
              [
                "PRESENT",
                "HALF_DAY",
              ].includes(
                status
              )
                ?
                row.checkIn
                :
                "",

            checkOut:
              [
                "PRESENT",
                "HALF_DAY",
              ].includes(
                status
              )
                ?
                row.checkOut
                :
                "",
          })
        )
    );

  };


  /*
  |--------------------------------------------------------------------------
  | SAVE
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async () => {

      if (
        !attendanceDate
      ) {

        setError(
          "Attendance date is required."
        );

        return;

      }


      if (
        rows.length ===
        0
      ) {

        setError(
          "No employees available."
        );

        return;

      }


      try {

        setLoading(
          true
        );


        setError(
          ""
        );


        const response =
          await api.post(
            config.saveBulkEmployeeAttendance,
            {
              attendanceDate,

              records:
                rows.map(
                  (
                    row
                  ) => ({
                    employeeId:
                      row.employeeId,

                    status:
                      row.status,

                    checkIn:
                      row.checkIn ||
                      null,

                    checkOut:
                      row.checkOut ||
                      null,

                    remark:
                      String(
                        row.remark ||
                        ""
                      ).trim(),
                  })
                ),
            }
          );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to save attendance."
          );

        }


        await onSuccess?.(
          response?.data
        );

      } catch (
        requestError
      ) {

        setError(
          getEmployeeApiError(
            requestError,
            "Unable to save attendance."
          )
        );

      } finally {

        setLoading(
          false
        );

      }

    };


  return ReactDOM
    .createPortal(

      <div className="employee-attendance-modal-root">

        <button
          type="button"
          className="employee-attendance-modal-backdrop"
          onClick={
            loading
              ?
              undefined
              :
              onClose
          }
          aria-label="Close"
        />


        <section className="employee-attendance-bulk-modal">

          <header>

            <div>

              <span>
                <FiUsers />
              </span>


              <div>

                <small>
                  DAILY ATTENDANCE
                </small>

                <h3>
                  Mark Employee Attendance
                </h3>

              </div>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
              disabled={
                loading
              }
            >

              <FiX />

            </button>

          </header>


          <div className="employee-attendance-bulk-body">

            <div className="employee-attendance-bulk-toolbar">

              <div className="employee-attendance-date-field">

                <label>
                  Attendance Date
                </label>

                <input
                  type="date"
                  value={
                    attendanceDate
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setAttendanceDate(
                        event
                          .target
                          .value
                      )
                  }
                />

              </div>


              <div className="employee-attendance-bulk-search">

                <FiSearch />

                <input
                  type="text"
                  value={
                    search
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setSearch(
                        event
                          .target
                          .value
                      )
                  }
                  placeholder="Search employee..."
                />

              </div>


              <div className="employee-attendance-mark-all">

                <button
                  type="button"
                  onClick={
                    () =>
                      markAll(
                        "PRESENT"
                      )
                  }
                >

                  <FiCheck />

                  All Present

                </button>


                <button
                  type="button"
                  onClick={
                    () =>
                      markAll(
                        "ABSENT"
                      )
                  }
                >
                  All Absent
                </button>

              </div>

            </div>


            {
              error
                ?
                (
                  <div className="employee-attendance-modal-error">
                    {error}
                  </div>
                )
                :
                null
            }


            <div className="employee-attendance-bulk-table-scroll">

              <table className="employee-attendance-bulk-table">

                <thead>

                  <tr>

                    <th>
                      Employee
                    </th>

                    <th>
                      Status
                    </th>

                    <th>
                      Check In
                    </th>

                    <th>
                      Check Out
                    </th>

                    <th>
                      Remark
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {
                    filteredRows.length ===
                    0
                      ?
                      (
                        <tr>

                          <td
                            colSpan="5"
                            className="employee-attendance-bulk-empty"
                          >
                            No employees found.
                          </td>

                        </tr>
                      )
                      :
                      filteredRows.map(
                        (
                          row
                        ) => {

                          const timeAllowed =
                            [
                              "PRESENT",
                              "HALF_DAY",
                            ].includes(
                              row.status
                            );


                          return (

                            <tr
                              key={
                                row.employeeId
                              }
                            >

                              <td>

                                <div className="employee-attendance-bulk-person">

                                  <strong>
                                    {row.name}
                                  </strong>

                                  <span>
                                    {
                                      row.employeeCode ||
                                      "-"
                                    }

                                    {
                                      row.department
                                        ?
                                        ` • ${row.department}`
                                        :
                                        ""
                                    }
                                  </span>

                                </div>

                              </td>


                              <td>

                                <select
                                  value={
                                    row.status
                                  }
                                  onChange={
                                    (
                                      event
                                    ) =>
                                      updateRow(
                                        row.employeeId,
                                        "status",
                                        event
                                          .target
                                          .value
                                      )
                                  }
                                >

                                  {
                                    ATTENDANCE_STATUSES.map(
                                      (
                                        status
                                      ) => (

                                        <option
                                          key={
                                            status
                                          }
                                          value={
                                            status
                                          }
                                        >
                                          {
                                            formatEmployeeLabel(
                                              status
                                            )
                                          }
                                        </option>

                                      )
                                    )
                                  }

                                </select>

                              </td>


                              <td>

                                <div className="employee-attendance-time-input">

                                  <FiClock />

                                  <input
                                    type="time"
                                    value={
                                      row.checkIn
                                    }
                                    disabled={
                                      !timeAllowed
                                    }
                                    onChange={
                                      (
                                        event
                                      ) =>
                                        updateRow(
                                          row.employeeId,
                                          "checkIn",
                                          event
                                            .target
                                            .value
                                        )
                                    }
                                  />

                                </div>

                              </td>


                              <td>

                                <div className="employee-attendance-time-input">

                                  <FiClock />

                                  <input
                                    type="time"
                                    value={
                                      row.checkOut
                                    }
                                    disabled={
                                      !timeAllowed
                                    }
                                    onChange={
                                      (
                                        event
                                      ) =>
                                        updateRow(
                                          row.employeeId,
                                          "checkOut",
                                          event
                                            .target
                                            .value
                                        )
                                    }
                                  />

                                </div>

                              </td>


                              <td>

                                <input
                                  type="text"
                                  value={
                                    row.remark
                                  }
                                  onChange={
                                    (
                                      event
                                    ) =>
                                      updateRow(
                                        row.employeeId,
                                        "remark",
                                        event
                                          .target
                                          .value
                                      )
                                  }
                                  placeholder="Optional"
                                />

                              </td>

                            </tr>

                          );

                        }
                      )
                  }

                </tbody>

              </table>

            </div>

          </div>


          <footer>

            <div>

              <span>
                {
                  rows.length
                }
                {" "}
                employees
              </span>

            </div>


            <div>

              <button
                type="button"
                onClick={
                  onClose
                }
                disabled={
                  loading
                }
              >
                Cancel
              </button>


              <button
                type="button"
                onClick={
                  handleSubmit
                }
                disabled={
                  loading
                }
              >

                <FiSave />

                {
                  loading
                    ?
                    "Saving..."
                    :
                    "Save Attendance"
                }

              </button>

            </div>

          </footer>

        </section>

      </div>,

      document.body

    );

};


export default BulkAttendanceModal;